import { useCallback, useContext, useEffect, useMemo } from 'react';
import { InteractionStatus } from '@azure/msal-browser';
import { useAccount, useIsAuthenticated, useMsal } from '@azure/msal-react';
import { AuthContext } from 'react-oauth2-code-pkce';

import { AuthMode } from '@/authentication';
import { getLoginRequest, getResetPasswordRequest } from '@/authentication/msal';
import { resetPasswordUrl, setAccessToken } from '@/authentication/oauth';
import { routes } from '@/lib/routes';

export const useAuth = () => {
  const oauthContext = useContext(AuthContext);
  const msalContext = useMsal();
  const msalAccount = useAccount();
  const msalIsAuthenticated = useIsAuthenticated();

  useEffect(() => {
    setAccessToken(oauthContext.token);
  }, [oauthContext.token]);

  const login = useCallback(async () => {
    switch (AuthMode) {
      case 'msal':
        await msalContext.instance.loginRedirect(getLoginRequest());
        break;
      case 'oauth':
        oauthContext.login();
        break;
      default:
        console.error('Invalid authentication mode');
        break;
    }
  }, [msalContext.instance, oauthContext]);

  const logout = useCallback(async () => {
    switch (AuthMode) {
      case 'msal':
        await msalContext.instance.logoutRedirect();
        break;
      case 'oauth':
        // TODO logout doesn't redirect to the logout page at the moment
        oauthContext.logOut();
        window.location.href = routes.logout();
        break;
      default:
        console.error('Invalid authentication mode');
        break;
    }
  }, [msalContext.instance, oauthContext]);

  const loginEmail = useMemo<string | null | undefined>(() => {
    switch (AuthMode) {
      case 'msal':
        return msalAccount?.username || msalAccount?.idTokenClaims?.emails?.[0] || msalAccount?.idTokenClaims?.email;
      case 'oauth': {
        return oauthContext.tokenData?.email;
      }
      default:
        console.error('Invalid authentication mode');
        return null;
    }
  }, [msalAccount, oauthContext]);

  const changePassword = useCallback(async () => {
    switch (AuthMode) {
      case 'msal':
        await msalContext.instance.loginRedirect(getResetPasswordRequest(loginEmail));
        break;
      case 'oauth':
        window.location.href = resetPasswordUrl;
        break;
      default:
        console.error('Invalid authentication mode');
        break;
    }
  }, [loginEmail, msalContext.instance]);

  // only the provider of the active mode is mounted; the other context is its library's default value,
  // and the OAuth default carries a placeholder token that would count as logged in
  const isAuthenticated = AuthMode === 'msal' ? msalIsAuthenticated : !!oauthContext.token;
  const isLoading =
    AuthMode === 'msal' ? msalContext.inProgress !== InteractionStatus.None : oauthContext.loginInProgress;

  return {
    isAuthenticated,
    isLoading,
    loginEmail,
    login,
    logout,
    changePassword,
  };
};
